// Shared SegmentPageBuilderContainer — works in both frontend and chat apps.
// API calls go through the SegmentPageBuilderApi adapter injected via context.

import { useMemo, useCallback, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { styled, createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import type { DashboardConfig } from '@trinity/chat/types/dashboard-config';
import { DashboardConfigSchema } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import { DashboardBuilderProvider } from '@trinity/chat-components/containers/Dashboard/DashboardBuilderProvider.js';
import { DashboardDataContainer } from '@trinity/chat-components/containers/Dashboard/DashboardDataContainer.js';
import { ConfigAwareDataProvider } from '@trinity/chat-components/containers/Dashboard/DataProvider.js';
import { createDefaultRegistry } from '@trinity/chat-components/components/PageDashboard/registry/default-registry.js';
import { DashboardBuilder } from '@trinity/chat-components/components/PageDashboard/Builder/DashboardBuilder.js';
import { BuilderCanvas } from '@trinity/chat-components/components/PageDashboard/Builder/BuilderCanvas.js';
import { WidgetPalette } from '@trinity/chat-components/components/PageDashboard/Builder/WidgetPalette.js';
import { useSegmentPage, useSavePageVersion, useRevertPageVersion } from './hooks';
import { SegmentWidgetPalette } from './SegmentWidgetPalette';
import { PageBuilderActionBar } from './PageBuilderActionBar';

type SegmentPageBuilderContainerProps = {
  projectId: number;
  segmentId: number;
  /** Override the action bar title (default: "Segment Overview") */
  title?: string;
  /** When true, hides the edit/save/cancel action bar */
  readOnly?: boolean;
};

export function SegmentPageBuilderContainer({
  projectId,
  segmentId,
  title,
  readOnly: forceReadOnly,
}: SegmentPageBuilderContainerProps): React.ReactElement | null {
  const pageQuery = useSegmentPage(projectId, segmentId);
  const saveVersionMutation = useSavePageVersion(projectId, segmentId);
  const revertMutation = useRevertPageVersion(projectId, segmentId);

  const [editing, setEditing] = useState(false);
  const isReadOnly = forceReadOnly || !editing;

  // Library picker modal state
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryCategory, setLibraryCategory] = useState<'chart' | 'kpi' | 'quote'>('chart');

  // Custom widget palette state
  const [customPaletteOpen, setCustomPaletteOpen] = useState(false);
  const [pendingOnSelect, setPendingOnSelect] = useState<((type: string) => void) | null>(null);

  const registry = useMemo(() => createDefaultRegistry(), []);

  const proxyRequest = useCallback(
    async (): Promise<CanonicalWidgetData | null> => null,
    [],
  );

  const dataProvider = useMemo(
    () => new ConfigAwareDataProvider(proxyRequest),
    [proxyRequest],
  );

  const initialConfig = useMemo((): DashboardConfig => {
    const raw = pageQuery.data?.config;
    if (!raw || Object.keys(raw).length === 0) return { blocks: [] };
    const result = DashboardConfigSchema.safeParse(raw);
    return result.success ? result.data : { blocks: [] };
  }, [pageQuery.data]);

  const handleSave = useCallback(
    async (config: DashboardConfig, description?: string): Promise<void> => {
      await saveVersionMutation.mutateAsync({ config, description });
      setEditing(false);
    },
    [saveVersionMutation],
  );

  const handleCancel = useCallback((): void => { setEditing(false); }, []);

  const handleRevert = useCallback(
    async (versionId: string): Promise<void> => {
      await revertMutation.mutateAsync({ versionId: Number(versionId) });
      setEditing(false);
    },
    [revertMutation],
  );

  // Custom palette for segment dashboards
  const CustomPaletteComponent = useMemo(() => {
    return function SegPalette(props: {
      open: boolean;
      onClose: () => void;
      onSelect?: (type: string) => void;
      hideSections?: boolean;
    }) {
      return (
        <SegmentWidgetPalette
          {...props}
          onLibraryOpen={(category) => { setLibraryCategory(category); setLibraryOpen(true); }}
          onCustomOpen={() => { setPendingOnSelect(() => props.onSelect ?? null); setCustomPaletteOpen(true); }}
        />
      );
    };
  }, []);

  const baseTheme = useTheme();
  // Use base theme as-is — custom primary override removed to avoid type conflicts
  const blueTheme = baseTheme;

  if (pageQuery.isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={32} />
      </LoadingContainer>
    );
  }

  if (!pageQuery.data) {
    return null;
  }

  return (
    <ThemeProvider theme={blueTheme}>
    <ShellCard>
      {!forceReadOnly && (
        <PageBuilderActionBar
          editing={editing}
          onEdit={() => setEditing(true)}
          onSave={() => setEditing(false)}
          onCancel={() => setEditing(false)}
          title={title}
        />
      )}
      <ContentArea>
        <DashboardBuilderProvider
          initialConfig={initialConfig}
          registry={registry}
          onSave={handleSave}
          onCancel={handleCancel}
          onRevert={handleRevert}
          showDataSource={true}
        >
          <DashboardDataContainer provider={dataProvider} mode={isReadOnly ? 'live' : 'builder'}>
            {isReadOnly ? (
              <BuilderCanvas readOnly />
            ) : (
              <DashboardBuilder title={pageQuery.data.name} />
            )}
          </DashboardDataContainer>

          {editing && (
            <WidgetPalette
              open={customPaletteOpen}
              onClose={() => setCustomPaletteOpen(false)}
              onSelect={(type) => {
                if (pendingOnSelect) pendingOnSelect(type);
                setCustomPaletteOpen(false);
                setPendingOnSelect(null);
              }}
              hideSections
            />
          )}
        </DashboardBuilderProvider>
      </ContentArea>
    </ShellCard>
    </ThemeProvider>
  );
}

const ShellCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const ContentArea = styled(Box)({
  flex: 1,
  overflow: 'auto',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '64px 0',
});
